<script lang="ts">
  import { cn } from "@/lib/utils";

  export let totalDistance: number;
  export let distanceUnit: string;
  export let totalElevation: number;
  export let elevationUnit: string;
  export let totalTimeSeconds: number;
  export let totalXss: number;
  export let revealed: boolean = false;
  export let color: string = '#3A86FF';

  const tidy = (n: number) => new Intl.NumberFormat().format(Math.round(n ?? 0));

  function getHours(seconds: number) {
    return tidy(Math.floor((seconds ?? 0) / 3600));
  }
</script>

<div class='w-full rounded-2xl p-[1px] training-card-shell'
  style='box-shadow: 0 10px 28px rgba(0,0,0,0.35), inset 0 0 8px rgba(255,255,255,0.05);'>
  <div class='rounded-2xl training-card-inner text-off-white'>
    <div class='w-full flex flex-col gap-5 p-5 overflow-hidden'>
      <!-- Header -->
      <div class='flex flex-row items-center gap-3'>
        <i class='fa-light fa-compass fa-2xl' style={`color:${color}; text-shadow: ${revealed ? `0 0 22px ${color}` : 'none'};`}></i>
        <span class='text-xl font-bold tracking-wide'>Training Totals</span>
      </div>

      <!-- Animated metric ribbons -->
      <div class='flex flex-col gap-3'>
        <div class={cn('ribbon', revealed ? 'ribbon-in-1' : '')}>
          <div class='icon-bubble'><i class='fa-light fa-road'></i></div>
          <div class='ribbon-text'>
            <div class='value'>{totalDistance} {distanceUnit}</div>
            <div class='label'>Total Distance</div>
          </div>
        </div>
        <div class={cn('ribbon', revealed ? 'ribbon-in-2' : '')}>
          <div class='icon-bubble'><i class='fa-light fa-mountain'></i></div>
          <div class='ribbon-text'>
            <div class='value'>{totalElevation} {elevationUnit}</div>
            <div class='label'>Elevation Gain</div>
          </div>
        </div>
        <div class={cn('ribbon', revealed ? 'ribbon-in-3' : '')}>
          <div class='icon-bubble'><i class='fa-light fa-clock'></i></div>
          <div class='ribbon-text'>
            <div class='value'>{getHours(totalTimeSeconds)} hrs</div>
            <div class='label'>Saddle Time</div>
          </div>
        </div>
        <div class={cn('ribbon', revealed ? 'ribbon-in-4' : '')}>
          <div class='icon-bubble'><i class='fa-light fa-bolt'></i></div>
          <div class='ribbon-text'>
            <div class='value'>{tidy(totalXss)} XSS</div>
            <div class='label'>Total Strain</div>
          </div>
        </div>
      </div>

      <!-- Decorative elevation profile strip -->
      <div class={cn('profile-strip', revealed ? 'profile-in' : '')}>
        <div class='profile-shape'></div>
        <div class='profile-shimmer'></div>
      </div>
    </div>
  </div>
</div>

<style>
  /* Unique card chrome */
  .training-card-shell {
    background: linear-gradient(5deg, rgba(18,20,22,1) 60%, rgba(40,44,48,0.18) 85%, rgba(58,134,255,0.10) 100%);
  }
  .training-card-inner {
    background:
      radial-gradient(circle at 24% 18%, rgba(255,255,255,0.06), transparent 40%),
      radial-gradient(circle at 76% 88%, rgba(255,255,255,0.04), transparent 40%),
      linear-gradient(180deg, rgba(18,20,22,0.95), rgba(8,9,10,0.98));
    border-radius: 1rem;
  }

  /* Metric ribbons */
  .ribbon {
    position: relative;
    display: flex; align-items: center; gap: 12px;
    padding: 12px 14px; border: 1px solid rgba(255,255,255,0.1); border-radius: 12px;
    background: linear-gradient(90deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03));
    box-shadow: 0 6px 18px rgba(0,0,0,0.24), inset 0 0 8px rgba(255,255,255,0.03);
    opacity: 0; transform: translateX(-14px) skewX(-3deg);
  }
  .icon-bubble { width: 36px; height: 36px; border-radius: 999px; display: grid; place-items: center; background: rgba(58,134,255,0.12); border: 1px solid rgba(58,134,255,0.25); }
  .icon-bubble i { color: #cfd8dc; text-shadow: 0 0 8px rgba(255,255,255,0.08); }
  .ribbon-text .value { font-size: 1.15rem; font-weight: 800; }
  .ribbon-text .label { font-size: 0.85rem; opacity: 0.85; }

  .ribbon-in-1 { animation: ribbon-in 420ms 80ms ease-out forwards; }
  .ribbon-in-2 { animation: ribbon-in 420ms 160ms ease-out forwards; }
  .ribbon-in-3 { animation: ribbon-in 420ms 240ms ease-out forwards; }
  .ribbon-in-4 { animation: ribbon-in 420ms 320ms ease-out forwards; }

  @keyframes ribbon-in {
    0% { opacity: 0; transform: translateX(-14px) skewX(-3deg) scale(0.98); }
    70%{ opacity: 1; transform: translateX(2px) skewX(0deg) scale(1.01); }
    100%{ opacity: 1; transform: translateX(0) skewX(0deg) scale(1); }
  }

  /* Decorative elevation profile */
  .profile-strip { position: relative; height: 24px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.1); overflow: hidden; opacity: 0; transform: translateY(10px); }
  .profile-shape {
    position: absolute; inset: 0;
    background:
      linear-gradient(180deg, rgba(58,134,255,0.14), rgba(58,134,255,0.06)),
      radial-gradient(120px 56px at 10% 100%, rgba(255,255,255,0.06), transparent 60%),
      radial-gradient(160px 80px at 38% 100%, rgba(255,255,255,0.06), transparent 60%),
      radial-gradient(200px 96px at 64% 100%, rgba(255,255,255,0.06), transparent 60%),
      radial-gradient(180px 72px at 88% 100%, rgba(255,255,255,0.06), transparent 60%);
    background-repeat: no-repeat;
    background-position: 0 0;
  }
  .profile-shimmer { position: absolute; inset: 0; background: linear-gradient(90deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.10) 12%, rgba(255,255,255,0.02) 24%); mix-blend-mode: screen; filter: blur(0.6px); opacity: 0.0; }
  .profile-in { animation: profile-in 460ms 380ms ease-out forwards; }
  @keyframes profile-in {
    0% { opacity: 0; transform: translateY(10px); }
    100%{ opacity: 1; transform: translateY(0); }
  }

  /* Footer note */
  .note { display: flex; align-items: center; gap: 8px; border: 1px solid rgba(255,255,255,0.10); background: rgba(255,255,255,0.05); border-radius: 12px; padding: 10px; opacity: 0; transform: translateY(10px); }
  .note-in { animation: note-in 420ms 420ms ease-out forwards; }
  @keyframes note-in { 0% { opacity: 0; transform: translateY(10px); } 100%{ opacity: 1; transform: translateY(0); } }
</style>
